import React, { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeApiKeysAPI } from '../services/api';
import { TimeApiKey } from '../types';
import TableLoadingRow from './TableLoadingRow';

const formatDate = (value?: string) => value ? new Date(value).toLocaleString() : '—';

const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<TimeApiKey[]>([]);
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadKeys = async () => {
    setLoading(true);
    try {
      setKeys(await timeApiKeysAPI.getMyKeys());
    } catch {
      toast.error('API keys could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || saving) {
      return;
    }

    setSaving(true);
    try {
      const key = await timeApiKeysAPI.createMyKey({ name: name.trim() });
      setCreatedKey(key.key);
      setName('');
      await loadKeys();
      toast.success('API key created');
    } catch {
      toast.error('API key could not be created');
    } finally {
      setSaving(false);
    }
  };

  const revokeKey = async (key: TimeApiKey) => {
    if (!window.confirm(`Revoke API key "${key.name}"?`)) {
      return;
    }

    try {
      await timeApiKeysAPI.revokeMyKey(key.id);
      await loadKeys();
      toast.success('API key revoked');
    } catch {
      toast.error('API key could not be revoked');
    }
  };

  const copyCreatedKey = async () => {
    if (!createdKey) {
      return;
    }

    await navigator.clipboard.writeText(createdKey);
    toast.success('API key copied');
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>API keys</h1>
          <p>Create keys for automations. Regular user keys can only read and edit your own time data.</p>
        </div>
      </div>

      {createdKey && (
        <div className="alert alert-warning api-key-secret" role="alert">
          <strong>Copy this key now.</strong> It will not be shown again.
          <code>{createdKey}</code>
          <button type="button" className="btn btn-secondary" onClick={copyCreatedKey}>Copy</button>
        </div>
      )}

      <form className="time-form card-section" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="api-key-name">Key name</label>
          <input
            id="api-key-name"
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={128}
            placeholder="e.g. CLI automation"
            required
          />
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={!name.trim() || saving}>
            <i className="fa-solid fa-key" aria-hidden="true" />
            {saving ? 'Creating...' : 'Create API key'}
          </button>
        </div>
      </form>

      <div className="table-responsive">
        <table className="table time-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Role</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableLoadingRow colSpan={7} label="Loading API keys" />
            ) : keys.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted">No API keys</td></tr>
            ) : keys.map(key => (
              <tr key={key.id}>
                <td data-label="Name">{key.name}</td>
                <td data-label="Prefix"><code>{key.keyPrefix}…</code></td>
                <td data-label="Role">{key.role}</td>
                <td data-label="Created">{formatDate(key.createdDate)}</td>
                <td data-label="Last used">{formatDate(key.lastUsedDate)}</td>
                <td data-label="Status">{key.isActive ? 'Active' : 'Revoked'}</td>
                <td data-label="Actions">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    type="button"
                    onClick={() => revokeKey(key)}
                    disabled={!key.isActive}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApiKeysPage;
